import React, { useState, useEffect, useCallback } from "react";
import PaymentInfoForm from "../Payment/PaymentInfoForm.js";
import DynamicLogoUpload from "../../common/DynamicLogoUpload/DynamicLogoUpload.js";
import CheckboxWithLabel from "../../elements/CheckBoxWithLabel/CheckBoxWithLabel";
import { useNavigate } from "react-router-dom";
import * as MUIStyled from "../../../MUIStyled";
import * as Styled from "../../../CompanyForm.styled";
import { DefaultLayout } from "../../common/Layout/DefaultLayout";
import { getPayments, postPayments } from "../../../config/fetchAPI.js";
import ProgressBarStepper from "../../elements/ProgressBar/ProgressBar";
import {
  BackButton,
  ButtonPurpleNext,
} from "../../elements/Buttons/Buttons.js";
import Loading from "../../elements/Loading/Loading.js";
import ReactMarkdown from "react-markdown";
import { sanitizeMarkdown } from "../../../utils/markdownUtils";

const formFields = [
  {
    label: "Company Name*",
    type: "text",
    layout: "fullwidth",
  },
  {
    label: "BSB*",
    type: "text",
    layout: "grid",
  },
  {
    label: "Account Number*",
    type: "text",
    layout: "grid",
  },
];

const currentStep = 5;
const totalSteps = 6;

export const PaymentInformationPage = () => {
  const [paymentDetails, setPaymentDetails] = useState([]);

  const [directDebitTermsData, setDirectDebitTermsData] = useState([]);
  const [eftTermsData, setEftTermsData] = useState([]);

  const [selectedPayment, setSelectedPayment] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const [hasErrorOccurred, setHasErrorOccurred] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [accountNameError, setAccountNameError] = useState("");
  const [bsbError, setBsbError] = useState("");
  const [accountNumberError, setAccountNumberError] = useState("");
  const [isHeaderUploaded, setIsHeaderUploaded] = useState(false);
  const [isLoadingS3State, setIsLoadingS3State] = useState(false);

  const [formData, setFormData] = useState({
    accountName: "",
    accountBSB: "",
    accountNumber: "",
    accepted: false,
  });

  const navigate = useNavigate();
  const associations = sessionStorage.getItem("associations");

  const isEftAllowed = paymentDetails?.allowEFT === true;

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [selectedPayment]);

  // if selectedPayment add to sessionStorage
  useEffect(() => {
    if (selectedPayment) {
      sessionStorage.setItem("selectedPayment", selectedPayment);
    }
  }, [selectedPayment]);

  // toggle to EFT or Direct Debit if previously selected
  useEffect(() => {
    if (paymentDetails.paymentMethod === "EFT") {
      setSelectedPayment("EFT");
    } else {
      setSelectedPayment("Direct Debit");
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (selectedPayment === "EFT") {
      setFormData({
        accountName: "",
        accountBSB: "",
        accountNumber: "",
        accepted: false,
      });
    }
  }, [selectedPayment]);

  // fall back to Direct Debit if EFT is not allowed
  useEffect(() => {
    if (!isLoading && !isEftAllowed && selectedPayment === "EFT") {
      setSelectedPayment("Direct Debit");
    }
  }, [isLoading, isEftAllowed, selectedPayment]);

  const fetchData = async () => {
    try {
      const [paymentsResponse] = await Promise.all([getPayments()]);
      setPaymentDetails(paymentsResponse);

      // get content by validator
      const termsContentNew = JSON.parse(associations).terms.filter(
        (term) => term.validator && ["EFT", "DD"].includes(term.validator),
      );

      // Split content by validator
      const directDebit = termsContentNew.filter(
        (term) => term.validator === "DD",
      );
      const eft = termsContentNew.filter((term) => term.validator === "EFT");
      setDirectDebitTermsData(directDebit);
      setEftTermsData(eft);
    } catch (err) {
      console.error("Error loading cached fields:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // POST Data
  const handlePostPayments = async () => {
    try {
      let payload;

      if (selectedPayment === "EFT") {
        // For EFT, only send accepted and paymentMethod
        payload = {
          accepted: paymentDetails.accepted || false,
          paymentMethod: selectedPayment,
        };
      } else {
        // For Direct Debit, include all account details
        payload = {
          ...formData,
          accountBSB: formData.accountBSB
            ? formData.accountBSB.replace(/-/g, "")
            : formData.accountBSB,
          paymentMethod: selectedPayment,
          accepted: paymentDetails.accepted || false,
        };
      }

      const response = await postPayments(payload);
      return response;
    } catch (error) {
      console.error("Error loading cached fields:", error);
      throw error;
    }
  };

  const handleFormChange = useCallback((updatedValues) => {
    setFormData((prevState) => ({
      ...prevState,
      ...updatedValues,
    }));
  }, []);

  // get selectedPayment from sessionStorage
  useEffect(() => {
    const storedSelectedPayment = sessionStorage.getItem("selectedPayment");
    if (storedSelectedPayment) {
      setSelectedPayment(storedSelectedPayment);
    }
  }, []);

  const togglePaymentMethod = (storedSelectedPayment) => {
    if (storedSelectedPayment === "Direct Debit") {
      setSelectedPayment("Direct Debit");
    } else if (storedSelectedPayment === "EFT") {
      setSelectedPayment("EFT");
    }
  };

  const handleCheckboxChange = (isChecked) => {
    setPaymentDetails((prev) => ({
      ...prev,
      accepted: isChecked,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setHasErrorOccurred(false);
    setAccountNameError("");
    setBsbError("");
    setAccountNumberError("");

    if (selectedPayment === "Direct Debit") {
      if (!formData.accountName) {
        setAccountNameError("Company name of account is required.");
        setErrorMessage("Company name of account is required.");
        setHasErrorOccurred(true);
        return;
      } else if (!formData.accountBSB) {
        setBsbError("BSB is required.");
        setErrorMessage("BSB is required.");
        setHasErrorOccurred(true);
        return;
      } else if (!/^\d{6}$/.test(formData.accountBSB.replace(/[-\s]/g, ""))) {
        setBsbError("BSB must be 6 digits.");
        setErrorMessage("BSB must be 6 digits.");
        setHasErrorOccurred(true);
        return;
      } else if (!formData.accountNumber) {
        setAccountNumberError("Account number is required");
        setErrorMessage("Account number is required");
        setHasErrorOccurred(true);
        return;
      } else if (!/^\d{6,}$/.test(formData.accountNumber.replace(/\s/g, ""))) {
        setAccountNumberError(
          "Account number must be at least 6 digits and contain numbers only.",
        );
        setErrorMessage(
          "Account number must be at least 6 digits and contain numbers only.",
        );
        setHasErrorOccurred(true);
        return;
      }
    }

    // Check if required terms are accepted
    const hasUnacceptedRequiredTerms = !paymentDetails.accepted;

    if (hasUnacceptedRequiredTerms) {
      setErrorMessage("Please check all the required terms and conditions.");
      setHasErrorOccurred(true);
      return;
    }

    try {
      const response = await handlePostPayments();

      // If response contains errors
      if (response.error) {
        setErrorMessage(response.error);
        setHasErrorOccurred(true);
        return;
      }

      if (!isHeaderUploaded && selectedPayment === "Direct Debit") {
        setErrorMessage("Please upload a bank statement header.");
        setHasErrorOccurred(true);
        return;
      }

      // Success - navigate to review
      navigate("/review");
    } catch (error) {
      setErrorMessage(
        "An error occurred while submitting payment information.",
      );
      setHasErrorOccurred(true);
    }
  };

  const handleBack = () => {
    navigate("/payrollTerms");
  };

  // check if header is uploaded - required if DD
  // disable next button while loading
  const handleImageUploaded = (logoURL, isLoadingS3) => {
    setIsLoadingS3State(isLoadingS3);
    if (logoURL && !isLoadingS3) {
      setIsHeaderUploaded(true);
    }
  };

  return (
    <DefaultLayout currentStep={currentStep}>
      {isLoading ? (
        <Loading />
      ) : (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <MUIStyled.CustomContainer>
            <Styled.MainTitleArrowWrapper style={{ marginBottom: "30px" }}>
              <BackButton
                onClick={handleBack}
                sx={{ marginLeft: "10%" }}
              />
              <Styled.MainTitle style={{ margin: "8px 0 0 0" }}>
                Payment information
              </Styled.MainTitle>
            </Styled.MainTitleArrowWrapper>

            <MUIStyled.PaymentTitleWrapper
              sx={{ width: "90%", marginBottom: "30px" }}
            >
              {isEftAllowed && (
                <Styled.BodyText
                  style={{
                    fontSize: "20px",
                    fontWeight: "600",
                    margin: "0 0 10px 0",
                  }}
                >
                  This direct debit section is optional.
                </Styled.BodyText>
              )}

              <Styled.BodyText style={{ fontWeight: "400" }}>
                {isEftAllowed
                  ? "If not completed, you will be required to ensure payment is received prior to the due date. Late payment will delay allocation of funds into driver accounts and non-payment to service providers."
                  : "Please provide your bank account details to complete setup and activate automatic payment processing."}
              </Styled.BodyText>
            </MUIStyled.PaymentTitleWrapper>

            {isEftAllowed && (
              <Styled.ToggleContainer>
                <Styled.ToggleOption
                  isActive={selectedPayment === "Direct Debit"}
                  onClick={() => togglePaymentMethod("Direct Debit")}
                >
                  Direct Debit
                </Styled.ToggleOption>

                <Styled.ToggleOption
                  isActive={selectedPayment === "EFT"}
                  onClick={() => togglePaymentMethod("EFT")}
                >
                  EFT
                </Styled.ToggleOption>
              </Styled.ToggleContainer>
            )}
            {selectedPayment === "Direct Debit" ? (
              <>
                <Styled.PaymentFormContainer>
                  {/* Payment Info Form */}
                  <PaymentInfoForm
                    setFormData={setFormData}
                    onChange={handleFormChange}
                    fields={formFields}
                    title="Contact Details Form"
                    accountNameError={accountNameError}
                    bsbError={bsbError}
                    accountNumberError={accountNumberError}
                    paymentDetails={paymentDetails}
                    sx={{
                      maxWidth: "600px",
                      marginBottom: "30px",
                      width: "100%",
                    }}
                    formData={formData}
                  />
                </Styled.PaymentFormContainer>
                <Styled.CompanyLogoContainer>
                  <DynamicLogoUpload
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      width: "100%",
                      margin: "auto",
                      position: "relative",
                    }}
                    titleText="Upload bank statement header*"
                    description={
                      <>
                        Upload the header of your bank account statement,
                        showing the Account name, BSB, and Account number to
                        allow verification of these details.
                      </>
                    }
                    supportedFormatsText="Supported formats: JPG, JPEG, PNG, GIF, WEBP, PDF"
                    buttonText="Upload Image"
                    onImageUploaded={handleImageUploaded}
                    setIsHeaderUploaded={setIsHeaderUploaded}
                    type="header"
                  />
                </Styled.CompanyLogoContainer>

                <Styled.AcknowledgementBox margin="0 0 30px 0">
                  {/* Title at the top */}
                  <Styled.BodyText
                    style={{
                      marginBottom: "20px",
                      fontSize: "20px",
                      fontWeight: "600",
                      textAlign: "left",
                    }}
                  >
                    Acknowledgement
                  </Styled.BodyText>

                  {directDebitTermsData.map((item) => {
                    const key = item.ID;
                    const isChecked = paymentDetails.accepted || false;

                    return (
                      <Styled.PaymentTermsCard key={key}>
                        <Styled.MarkdownContent>
                          <ReactMarkdown>
                            {sanitizeMarkdown(item.Content)}
                          </ReactMarkdown>
                        </Styled.MarkdownContent>

                        <CheckboxWithLabel
                          checked={isChecked}
                          onChange={(event) =>
                            handleCheckboxChange(event.target.checked)
                          }
                          sx={{
                            width: "100%",
                            textAlign: "left",
                            margin: "10px 0 0 -22px",
                          }}
                          label={
                            <Styled.CheckboxLabel>
                              I accept
                            </Styled.CheckboxLabel>
                          }
                        />
                      </Styled.PaymentTermsCard>
                    );
                  })}
                </Styled.AcknowledgementBox>

                {/* <Styled.PaymentTermsCard>
                                    {directDebitTermsData.map((item) => {
                                        const key = item.ID;
                                        const isChecked =
                                            paymentDetails.accepted || false;

                                        return (
                                            <Styled.PaymentTermsCard key={key}>
                                                <Styled.MarkdownContent>
                                                    <ReactMarkdown>
                                                        {item.Content || ""}
                                                    </ReactMarkdown>
                                                </Styled.MarkdownContent>

                                                <CheckboxWithLabel
                                                    checked={isChecked}
                                                    onChange={(event) =>
                                                        handleCheckboxChange(
                                                            event.target.checked
                                                        )
                                                    }
                                                    sx={{
                                                        width: "100%",
                                                        textAlign: "left",
                                                        margin: "10px 0 0 -22px",
                                                    }}
                                                    label={
                                                        <Styled.CheckboxLabel>
                                                            I accept
                                                        </Styled.CheckboxLabel>
                                                    }
                                                />
                                            </Styled.PaymentTermsCard>
                                        );
                                    })}
                                </Styled.PaymentTermsCard> */}
              </>
            ) : (
              <>
                <Styled.AcknowledgementBox margin="0 0 30px 0">
                  <Styled.BodyText
                    style={{
                      marginBottom: "20px",
                      fontSize: "20px",
                      fontWeight: "600",
                      textAlign: "left",
                    }}
                  >
                    Acknowledgement
                  </Styled.BodyText>

                  {eftTermsData.map((item) => {
                    const key = item.ID;
                    const isChecked = paymentDetails.accepted || false;

                    return (
                      <Styled.PaymentTermsCard key={key}>
                        <Styled.PaymentTermsBody
                          dangerouslySetInnerHTML={{
                            __html:
                              item.Content?.replace(/\n\n/g, "<br/><br/>") ||
                              "",
                          }}
                        />

                        <CheckboxWithLabel
                          checked={isChecked}
                          onChange={(event) =>
                            handleCheckboxChange(event.target.checked)
                          }
                          sx={{
                            width: "100%",
                            textAlign: "left",
                            margin: "10px 0 0 -22px",
                          }}
                          label={
                            <Styled.CheckboxLabel>
                              I accept
                            </Styled.CheckboxLabel>
                          }
                        />
                      </Styled.PaymentTermsCard>
                    );
                  })}
                </Styled.AcknowledgementBox>
              </>
            )}

            {hasErrorOccurred && (
              <MUIStyled.CustomAlert severity="error">
                {errorMessage}
              </MUIStyled.CustomAlert>
            )}

            {paymentDetails === "Error" && (
              <MUIStyled.CustomAlert severity="error">
                An internal error occurred, please contact support.
              </MUIStyled.CustomAlert>
            )}

            <MUIStyled.CustomGrid
              container
              justifyContent={{ xs: "center", sm: "flex-end" }}
              alignSelf={"flex-end"}
              marginRight={"25px"}
            >
              <ButtonPurpleNext
                type="submit"
                onClick={handleSubmit}
                disabled={paymentDetails === "Error" || isLoadingS3State}
                style={{ marginTop: "40px" }}
              >
                Next
              </ButtonPurpleNext>
            </MUIStyled.CustomGrid>
          </MUIStyled.CustomContainer>

          <Styled.ProgressBarContainerWrapper>
            {currentStep <= totalSteps && (
              <ProgressBarStepper
                currentStep={currentStep}
                totalSteps={totalSteps}
              />
            )}
          </Styled.ProgressBarContainerWrapper>
        </div>
      )}
    </DefaultLayout>
  );
};
